import { zodResolver } from "@hookform/resolvers/zod";
import {
	SiInstagram,
	SiSnapchat,
	SiSpotify,
	SiX,
} from "@icons-pack/react-simple-icons";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Link2, Loader2, UserIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { UserProfileResponse } from "~/lib/api/types";
import { userApi } from "~/lib/api/user";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Form, FormField, FormItem, FormMessage } from "../ui/form";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const socialInfoSchema = z.object({
	instagram: z.string().optional(),
	twitter: z.string().optional(),
	snapchat: z.string().optional(),
	spotify: z.string().optional(),
	appleMusic: z.string().optional(),
});

type SocialInfoForm = z.infer<typeof socialInfoSchema>;

const socialPlatforms = [
	{
		label: "Instagram",
		value: "instagram",
		icon: SiInstagram,
		placeholder: "instagram.com",
	},
	{
		label: "Twitter",
		value: "twitter",
		icon: SiX,
		placeholder: "twitter.com",
	},
	{
		label: "Snapchat",
		value: "snapchat",
		icon: SiSnapchat,
		placeholder: "snapchat.com",
	},
	{
		label: "Spotify",
		value: "spotify",
		icon: SiSpotify,
		placeholder: "spotify.com",
	},
] as const;

type SocialInfoProps = {
	socials: UserProfileResponse["socials"];
};

export function SocialInfo({ socials }: SocialInfoProps) {
	const router = useRouter();
	const [isEditing, setIsEditing] = useState(false);
	const { mutateAsync: updateProfile, isPending } = useMutation({
		mutationFn: (data: SocialInfoForm) => {
			return userApi.updateSocials(data);
		},
		onSuccess: async () => {
			await router.invalidate();
		},
	});
	const form = useForm<SocialInfoForm>({
		resolver: zodResolver(socialInfoSchema),
		defaultValues: {
			instagram:
				socials.find((social) => social.platform === "instagram")?.username ??
				"",
			twitter:
				socials.find((social) => social.platform === "twitter")?.username ?? "",
			snapchat:
				socials.find((social) => social.platform === "snapchat")?.username ??
				"",
			spotify:
				socials.find((social) => social.platform === "spotify")?.username ?? "",
		},
		disabled: !isEditing || isPending,
	});

	async function onSubmit(data: SocialInfoForm) {
		await updateProfile(data);
		setIsEditing(false);
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit, (err) => {
					console.error(err);
				})}
				className="space-y-4"
			>
				<Card className="bg-white/5 border-white/10 overflow-hidden rounded-xl">
					<CardHeader className="p-5 border-b border-white/10 bg-white/5 flex items-center flex-row">
						<h3 className="text-lg font-medium flex items-center gap-2 text-white">
							<UserIcon className="w-4 h-4 text-accent" />
							Social Profiles
						</h3>
						<Button
							variant="secondary"
							size="sm"
							className="ml-auto"
							onClick={() => {
								if (isEditing) {
									form.handleSubmit(onSubmit)();
								} else {
									setIsEditing(true);
								}
							}}
							type="button"
							disabled={isPending}
						>
							{isEditing ? "Save" : "Edit"}
							{isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
						</Button>
						{isEditing ? (
							<Button
								variant="secondary"
								size="sm"
								className="ml-2"
								onClick={() => {
									setIsEditing(false);
								}}
								type="button"
								disabled={isPending}
							>
								Cancel
							</Button>
						) : null}
					</CardHeader>
					<CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
						{socialPlatforms.map((platform) => (
							<div className="space-y-2" key={platform.value}>
								<FormField
									control={form.control}
									name={platform.value}
									render={({ field }) => (
										<FormItem>
											<Label
												htmlFor={platform.value}
												className="text-white/70 text-sm flex items-center gap-1.5"
											>
												<platform.icon className="w-3.5 h-3.5 text-accent/70" />
												{platform.label}
											</Label>
											<div className="flex items-center">
												<Input
													id={platform.value}
													placeholder={`https://${platform.placeholder}`}
													className={cn(
														"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20 flex-1 shrink-0 rounded-tr-none rounded-br-none",
													)}
													disabled={field.disabled}
													{...field}
												/>
												{socials.find(
													(social) => social.platform === platform.value,
												)?.link && (
													<a
														href={
															socials.find(
																(social) => social.platform === platform.value,
															)?.link
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex-shrink-0 size-9 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center rounded-tl-none rounded-bl-none"
													>
														<Link2 className="h-4 w-4 text-white/70" />
													</a>
												)}
											</div>
											<FormMessage className="text-red-500 text-sm mt-1" />
										</FormItem>
									)}
								/>
							</div>
						))}
					</CardContent>
				</Card>
			</form>
		</Form>
	);
}
